import React, { useState, useEffect } from 'react';
import './EscalationForm.css';

/**
 * EscalationForm Component
 * Renders a dynamic form based on escalation schema
 * 
 * @param {Object} props
 * @param {Object} props.schema - Form schema configuration
 * @param {string} props.sessionId - Current session ID
 * @param {Function} props.onSuccess - Success callback with ticket data
 * @param {Function} props.onCancel - Cancel callback
 * @param {boolean} props.isSubmitting - Whether form is being submitted
 * @param {Object} props.errors - Form validation errors
 * @param {Function} props.onSubmit - Form submission handler
 */
const EscalationForm = ({
  schema,
  sessionId,
  onSuccess,
  onCancel,
  isSubmitting = false,
  errors = {},
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    session_id: sessionId,
  });

  // Load saved form state if available
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('escalation_draft');
      if (saved) {
        const savedData = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...savedData }));
      }
    } catch (error) {
      console.warn('Failed to load saved form state:', error);
    }
  }, []);

  // Save form state on changes
  useEffect(() => {
    try {
      sessionStorage.setItem('escalation_draft', JSON.stringify(formData));
    } catch (error) {
      console.warn('Failed to save form state:', error);
    }
  }, [formData]);

  /**
   * Handle input field changes
   * @param {string} name - Field name
   * @param {string} value - Field value
   */
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (onSubmit) {
      const success = await onSubmit(formData);
      if (success) {
        // Clear saved form state on successful submission
        try {
          sessionStorage.removeItem('escalation_draft');
        } catch (error) {
          console.warn('Failed to clear form state:', error);
        }
      }
    }
  };

  /**
   * Render individual form field based on type
   * @param {Object} field - Field configuration
   * @returns {JSX.Element} - Rendered field
   */
  const renderField = (field) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const fieldId = `escalation-${field.name}`;

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <div key={field.name} className="form-field">
            <label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="required" aria-label="required">*</span>}
            </label>
            <input
              id={fieldId}
              type={field.type}
              name={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              maxLength={field.max_length}
              className={error ? 'error' : ''}
              disabled={isSubmitting}
              aria-required={field.required}
              aria-describedby={error ? `${fieldId}-error` : undefined}
              autoComplete={field.type === 'email' ? 'email' : field.name === 'contact_name' ? 'name' : 'off'}
            />
            {field.max_length && (
              <small className="char-count" aria-live="polite">
                {value.length}/{field.max_length}
              </small>
            )}
            {error && (
              <span 
                id={`${fieldId}-error`}
                className="error-message" 
                role="alert"
                aria-live="polite"
              >
                {error}
              </span>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.name} className="form-field">
            <label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="required" aria-label="required">*</span>}
            </label>
            <select
              id={fieldId}
              name={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className={error ? 'error' : ''}
              disabled={isSubmitting}
              aria-required={field.required}
              aria-describedby={error ? `${fieldId}-error` : undefined}
            >
              <option value="">Select {field.label}</option>
              {field.options?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {error && (
              <span 
                id={`${fieldId}-error`}
                className="error-message" 
                role="alert"
                aria-live="polite"
              >
                {error}
              </span>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="form-field">
            <label htmlFor={fieldId}>
              {field.label}
              {field.required && <span className="required" aria-label="required">*</span>}
            </label>
            <textarea
              id={fieldId}
              name={field.name}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              rows={4}
              maxLength={field.max_length}
              className={error ? 'error' : ''}
              disabled={isSubmitting}
              aria-required={field.required}
              aria-describedby={error ? `${fieldId}-error` : undefined}
            />
            {field.max_length && (
              <small className="char-count" aria-live="polite">
                {value.length}/{field.max_length}
              </small>
            )}
            {error && (
              <span 
                id={`${fieldId}-error`}
                className="error-message" 
                role="alert"
                aria-live="polite"
              >
                {error}
              </span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Focus trap for accessibility
  useEffect(() => {
    const modal = document.querySelector('.escalation-form-modal');
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => modal.removeEventListener('keydown', handleTabKey);
  }, []);

  return (
    <div className="escalation-form-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div 
        className="escalation-form-modal" 
        role="dialog" 
        aria-labelledby="escalation-title" 
        aria-modal="true"
      >
        <div className="escalation-form-header">
          <h2 id="escalation-title">Contact Human Support</h2>
          <button 
            className="close-button" 
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="escalation-form" role="form" aria-label="Escalation form">
          {schema?.fields?.map(renderField)}
          
          {errors.submit && (
            <div className="error-message submit-error" role="alert" aria-live="polite">
              {errors.submit}
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={onCancel}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              className="submit-button"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EscalationForm;
